/**
 * Responsive Design System
 * 
 * Platform-aware sizing and spacing constants.
 * Provides different values for mobile vs desktop/tablet views.
 * 
 * Similar to theme system but for dimensions/spacing.
 * Use these constants instead of hardcoded pixel values for responsive design.
 * 
 * Usage:
 * const responsive = useResponsiveDesign();
 * const avatarSize = responsive.avatar.small;
 * const iconSize = responsive.icon.medium;
 */

import React from 'react';

/**
 * Breakpoint in pixels where mobile layout switches to desktop
 * Matches common mobile device max width
 */
const MOBILE_BREAKPOINT = 768;

/**
 * Check if current viewport is mobile size
 */
export const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
};

/**
 * Check if current viewport is landscape orientation
 */
export const isLandscapeViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > window.innerHeight;
};

/**
 * Platform type
 */
type Platform = 'mobile' | 'desktop';

/**
 * Complete responsive design system structure
 */
export interface ResponsiveDesign {
  avatar: {
    xs: number;
    small: number;
    medium: number;
    large: number;
    xlarge: number;
    xxlarge?: number;
    xxxlarge?: number;
  };
  border: {
    /**
     * Sub-pixel divider, thinner than `thin`. Renders as a faint sliver rather
     * than a crisp rule — used for the separators between settings-card rows.
     * Breakpoint-independent by design (a hairline does not scale up).
     */
    hairline: number;
    thin: number;
    medium: number;
    thick: number;
  };
  /**
   * Opacity tokens for non-color visual states. Platform-independent (same
   * value on mobile + desktop) but kept on the responsive scale so usages
   * reference a named token instead of inlining a numeric literal.
   */
  opacity: {
    /** Dimmed/disabled affordance for inert controls (e.g. a button gated off while a dependency resolves). */
    disabled: number;
  };
  borderRadius: {
    sm: number;
    base: number;
    md: number;
    lg: number;
    xl: number;
    '2xl': number;
    'round'?: string;
  };
  font: {
    xs: string;
    sm: string;
    base: string;
    menu: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
    '5xl': string;
    '10xl': string;
  };
  fontWeight: {
    regular: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  spacing: {
    xxxs: number;
    xxs: number;
    xs: number;
    sm: number;
    base: number;
    lg: number;
    xl: number;
    '2xl': number;
    '3xl': number;
  };
  gap: {
    xs: number;
    sm: number;
    base: number;
    lg: number;
    xl: number;
    '2xl': number;
    '3xl': number;
    '4xl': number;
    '5xl'?: number;
    '6xl'?: number;
  };
  icon: {
    xxs: number;
    xs: number;
    small: number;
    medium: number;
    large: number;
    xlarge: number;
    xxlarge?: number;
  };
  action: {
    imageSize: number;
  };
  button: {
    size: {
      xxs: number;
      xs: number;
      small: number;
      medium: number;
      large: number;
      xl: number;
      xxl: number;
      xxxl: number;
      sl: number;
      sl2: number;
    };
    padding: {
      xs: string;
      small: string;
      medium: string;
      large: string;
      xl: string;
      xxl: string;
      xxxl: string;
    };
    borderRadius: {
      xs: number;
      sm: number;
      base: number;
      md: number;
      lg: number;
      xl: number;
    };
    border: string;
  };
  progressBar: {
    height: number;
  };
  bottomSheet: {
    radius: number;
    maxHeight: string;
    topBar: {
      dragIndicator: {
        width: string;
        height: string;
        borderRadius: string;
      }
    }
  },
  dialog: {
    borderRadius: string;
    width: string;
    maxWidth: string;
    maxHeight: string;
  },
  infoDialog: {
    maxWidth: string;
  };
  /**
   * Caps for the inline chat media player (image + video branches on the
   * media-details page). `maxWidth` keeps the player from sprawling on wide
   * desktop layouts; `maxHeight` keeps it within the viewport.
   */
  mediaPlayer: {
    maxWidth: string;
    maxHeight: string;
  };
  /**
   * ThemeSelector sheet-content card. The in-content "Theme Preference"
   * heading (a 28px `text-lg` line box + 16px `mb-4` = 44px) was removed on
   * `fix_theme_selector_theming_cleanup`; this padding replaces that vertical
   * mass so the card does not read cramped in mobile bottom-sheet mode
   * (task prompt: "…as it will look too small on mobile devices when in
   * bottom sheet mode after we'll remove the title field"). Sized so the card
   * is no shorter than it was pre-branch (2 × 48 ≥ 24 + 44 + 24).
   * Web-only surface — no Flutter counterpart.
   */
  themeSelector: {
    contentPaddingVertical: number;
  };
  /**
   * Safe area insets for devices with notches, home indicators, etc.
   * These are CSS string values using env() that can be used in inline styles.
   * Values are dynamically evaluated by the browser and update on orientation change.
   *
   * @example
   * paddingTop: responsive.safeArea.top
   * paddingTop: `calc(${responsive.safeArea.top} + 16px)`
   */
  safeArea: {
    top: string;
    right: string;
    bottom: string;
    left: string;
  },
  stepper: {
    progressHeight: number;
    progressGap: number;
    progressBorderRadius: number;
  };
  form: {
    textareaHeight: number;
  };
  contentActionsSheet: {
    fixedHeight: string;
  };
  userActionsSheet: {
    fixedHeight: string;
  };
  /**
   * Language picker sheet (`LanguageSelectorSheet`).
   *
   * Web-only surface — the Flutter app ships a single hardcoded language and has
   * no picker, so there is no Flutter counterpart to match. The height is fixed
   * on purpose: the sheet body owns its own scroller, so the language list
   * scrolls *inside* the shell instead of growing it (an unbounded sheet is the
   * bug this token exists to prevent).
   *
   * The value is `40dvh`, deliberately shorter than the `60dvh` of
   * `contentActionsSheet` / `userActionsSheet` — those bodies carry five to
   * eight rows, this one is a short picker. After the shell's own title row
   * (~56px mobile / ~67px desktop) and the body's `spacing.base` padding, it
   * leaves room for roughly four `ListItemTile` language rows: enough that the
   * current short list does not float in a mostly-empty panel, while the body's
   * scroller still takes over the moment the list outgrows the shell.
   *
   * Both tiers carry the same fraction **by decision, not by copy** (matching
   * every sibling sheet height except `addContentSheet`): a `dvh` value is
   * viewport-relative, and a desktop row (`font.lg` title + `font.sm`
   * description + `spacing.base` padding) plus the desktop title row grow by
   * roughly the same factor as the desktop viewport itself, so an identical
   * fraction surfaces the same number of rows on both tiers.
   */
  languageSelectorSheet: {
    fixedHeight: string;
  };
  reportContentSheet: {
    fixedHeight: string;
  };
  reportUserSheet: {
    fixedHeight: string;
  };
  appealUserReportSheet: {
    fixedHeight: string;
  };
  searchUsersSheet: {
    width: string;
  };
  selectContentTypeSheet: {
    width: string;
    fixedHeight: string;
  };
  addContentSheet: {
    width: string;
    fixedHeight: string;
  };
  addFeedback: {
    /** Fixed height for the LocalMediaSheet surface (SheetService `fixedHeight`). */
    sheetFixedHeight: string;
    /** Max height of the aspect-ratio media box inside the sheet (drives its padding). */
    localMediaMaxHeight: string;
  };
  dropZone: {
    height: number;
    padding: number;
    borderRadius: number;
  };
  videoPreview: {
    thumbnailSize: number;
  };
  topBar: {
    /** Height of the main navigation bar and the FlexibleHeaderList app bar in pixels. */
    height: number;
    /** Blur radius for the top bar backdrop filter. */
    backdropBlur: number;
    /** Letter spacing in pixels applied to the active nav item. Used in both CSS and canvas measurement. */
    activeLetterSpacing: number;
  };
  /**
   * Static page app bar (`ExpauseAppBar`). Its box height is
   * `calc(topBar.height + safeArea.top)`, so only the blur radius is its own
   * token; the height is deliberately shared with `topBar.height` so the two
   * app-bar families line up.
   */
  appBar: {
    /**
     * Blur radius (px) of the page app bar's translucent backdrop.
     *
     * Set to the literal blur `FlexibleHeaderList`'s own bar already renders
     * (`blur(10px)`), so the two coexisting app bars read as one surface when a
     * user moves between a list page and a static page. Intentionally lighter
     * than the home `TopBar`'s `topBar.backdropBlur: 24`, and it deliberately
     * does NOT track Flutter's `DimensionConstants.appBarBlurX/Y = 20`
     * (`lib/domain/constants/dimensions_constants.dart:115-116`) — blur radius
     * is a styling idiom the web may set on its own
     * (`presentation.md` → "Web vs Flutter UI parity scope"). Do not "fix" this
     * to 20.
     */
    backdropBlur: number;
  };
  separator: {
    vertical: number;
    horizontal: number;
  };
  divider: {
    small: number;
    big: number;
  };
  walletCard: {
    minHeight: number;
    maxWidth: number;
  };
  toggle: {
    thumbPadding: number;
    shadowBlur: number;
    shadowOffset: number;
  };
  flexibleHeader: {
    height: number;
    borderRadius: number;
    iconContainerSize: number;
    iconContainerBorderRadius: number;
  };
  list: {
    /**
     * `defaultItemHeight` estimate forwarded to `<VirtualList>` for
     * avatar-plus-two-text-lines rows (e.g. `LatestUsersContent`). Rough
     * estimate: avatar (`avatar.medium`) + display-name line + two timestamp
     * lines + the row's own `spacing.lg` bottom padding. Like the chat token,
     * this only budgets the initial render window — the list re-measures each
     * row, so an approximate value is fine.
     */
    defaultItemHeight: number;
  };
  chat: {
    /**
     * Width of the diagonal strike-through bar overlaid on the video-call
     * cam icon when the call failed. Flutter parity: 1.5 px
     * (`chat_screen.dart:1854` / `:2994`).
     */
    strikeWidth: number;
    /**
     * Blur radius applied to the full-screen blocked-overlay backdrop in
     * `BlockedOverlays`. Flutter parity: 10 px on both platforms
     * (`DimensionConstants.overlayBlurX/Y`, used by `BackdropFilter` in
     * `chat_screen.dart:1019` / `:1053` / `:1087`).
     */
    overlayBlur: number;
    /**
     * `defaultItemHeight` hint passed to `VirtualList<ChatMessageEntity>` in
     * `ChatMessagesList`. Rough estimate for a text-bubble row plus the row's
     * vertical gap — sized intentionally on the lower side: `VirtualList(reversed)`
     * only uses this to budget the initial render window, and after the bottom-anchor
     * settle loop tall bubbles (image/video, multi-line text) still land at the
     * true bottom.
     */
    defaultItemHeight: number;
    /**
     * Minimum width applied to text-bubble containers (`TextBubble`).
     *
     * Web-only addition (no Flutter parity): Flutter's `Linkify` widget in
     * `chat_screen.dart:1438` / `:2545` lets the bubble shrink to the text's
     * natural width, but on the web — with line-height + vertical padding —
     * a 1-2 character message renders taller than it is wide. This token
     * keeps the bubble at least square-ish for very short messages. Sized
     * to roughly match the bubble's rendered height for a single line
     * (`spacing.sm` * 2 + `font.lg` line-box ≈ ~40 px mobile / ~48 px
     * desktop).
     */
    bubbleMinWidth: number;
    /**
     * Max width applied to text-bubble container rows in `MessageBubble`.
     *
     * Mirrors Flutter's `ConstrainedBox(maxWidth: MediaQuery.size.width * 3/4)`
     * at `chat_screen.dart:1341-1347` (own) / `:2428-2434` (peer). Lives on
     * the responsive scale next to `bubbleMinWidth`. Expressed as a CSS
     * length string (e.g. `'75vw'`) so it can be dropped straight into
     * inline styles.
     */
    bubbleMaxWidth: string;
    /**
     * Px distance from the bottom edge at which `VirtualList(reversed)`
     * flips between "at bottom" and "scrolled away from bottom" for the
     * chat surface. Forwarded as `scrolledAwayFromBottomThreshold` to
     * `<VirtualList>` from `ChatMessagesList`.
     *
     * Web-only sizing intent (no direct Flutter parity — Flutter uses
     * `_currentScroll <= 0` at `chat_screen.dart:340-356, 547-579`): the
     * chat surface wants a generous-enough buffer that a user lingering
     * near the bottom is still treated as at-bottom (i.e., new peer
     * messages keep auto-scrolling instead of stacking into the unread
     * banner). The VirtualList default of `100` is too tight here; chat
     * gets a wider buffer on both platforms, and an even wider one on
     * desktop where viewport heights are larger.
     */
    scrolledAwayThreshold: number;
  };
  community: {
    /**
     * `defaultItemHeight` hint passed to `VirtualList<CommunityChatMessageEntity>`
     * in `CommunityMessagesList`. Rough estimate for a text bubble plus the
     * author-info row (avatar + name + timestamp) and the row's vertical gap —
     * sized on the lower side: `VirtualList(reversed)` only uses it to budget the
     * initial render window, and the bottom-anchor settle loop re-measures, so
     * taller rows (reply preview + main bubble) still land at the true bottom.
     */
    defaultItemHeight: number;
    /**
     * Px distance from the bottom edge at which `VirtualList(reversed)` flips
     * between "at bottom" and "scrolled away from bottom" for the community
     * surface. Forwarded as `scrolledAwayFromBottomThreshold` to `<VirtualList>`
     * from `CommunityMessagesList`. Web-only sizing (Flutter uses
     * `_currentScroll <= 0` at `community_screen.dart:235-239`) — matched to the
     * chat surface's generous buffer so a user lingering near the bottom is still
     * treated as at-bottom.
     */
    scrolledAwayThreshold: number;
  };
  /**
   * Sizing for the 1:1 video-call page's local self-view PiP and its corner
   * inset (web port of Flutter `video_call_screen.dart:917-1035`).
   *
   * Web-only sizing intent (no 1:1 Flutter parity): Flutter's
   * `DimensionConstants.videoCallRemoteViewWidth = 80` PiP at 4:3 is tuned for a
   * phone viewport; on a desktop browser the same 80 px box would be barely
   * visible. `pipWidth` keeps the 4:3 aspect (height = 4/3 * width, matching
   * Flutter `:949`) but scales it up for the larger viewport, and `pipInset` is
   * the top/right corner offset (Flutter's fixed `16` + `viewPadding.top`,
   * `:932-933`). `pipBorderRadius` matches the Flutter `8` rounded corner
   * (`:935`).
   */
  videoCall: {
    /** Width of the local self-view PiP box (height = 4/3 * width, 4:3). */
    pipWidth: number;
    /** Top/right corner inset of the PiP from the viewport edge. */
    pipInset: number;
    /** Corner radius of the PiP box (Flutter `BorderRadius.circular(8)`, `:935`). */
    pipBorderRadius: number;
    /** Size of each bottom-bar control icon (Flutter `liveActionsIconSize`, `:1241`). */
    controlIconSize: number;
    /** Square touch-target around each control icon (web hit area). */
    controlHitSize: number;
    /** Gap between the control buttons in the bottom bar. */
    controlGap: number;
    /** Vertical padding of the bottom control bar (Flutter `:1124-1127`). */
    controlBarPaddingY: number;
    /** Horizontal padding of the bottom control bar (Flutter `:1238`/`:1318`). */
    controlBarPaddingX: number;
    /**
     * Outer offset that floats the bottom control bar off the viewport edges
     * (web-only deviation from Flutter's edge-to-edge `_bottomWidget`). Applied
     * as the bottom inset (on top of `safeArea.bottom`) and as the left/right
     * margins so the bar reads as a centred floating card.
     */
    controlBarFloatGap: number;
    /**
     * Corner radius of the bottom control bar. Once the bar floats off the
     * bottom edge (web deviation) all four corners carry this radius (was
     * top-only, mirroring Flutter `bottomOverlayDecorationVideoCall` `60`,
     * `app_styles.dart:371-377`).
     */
    controlBarRadius: number;
    /** Stroke width of the mute/camera-off strike line (Flutter `1.5`, `:1266`/`:1300`). */
    controlStrikeWidth: number;
    /** Icon size inside the send-gift circle (Flutter `actionIconSize`, `:1213`). */
    giftIconSize: number;
    /** Padding around the gift icon inside its circle (Flutter `EdgeInsets.all(16)`, `:1210`). */
    giftButtonPadding: number;
  };
  /**
   * Sizing for the broadcaster-only live-streaming control column (web port of
   * Flutter `_streamActions`, `live_streaming_screen.dart:1464-1635`) — a
   * right-aligned vertical stack of circular icon buttons (switch camera / mute /
   * camera off / end live). Flutter positions it `top: toolbarHeight +
   * viewPadding.top + 12, right: 8` (`:1472-1477`); the web mirrors that with a
   * top offset that clears the top bar + the device safe-area inset, and a right
   * inset off the viewport edge.
   */
  liveStreaming: {
    /** Size of each control icon (Flutter `actionIconSize`, `:1498`). */
    controlIconSize: number;
    /** Diameter of the circular control button (icon + Flutter `EdgeInsets.all(8)`, `:1495`). */
    controlButtonSize: number;
    /** Vertical gap between the buttons in the column (Flutter `EdgeInsets.only(bottom: 24)`, `:1488`). */
    controlColumnGap: number;
    /** Right inset of the control column from the viewport edge (Flutter `right: 8`, `:1477`). */
    controlColumnRightInset: number;
    /** Top gap below the top bar for the control column (Flutter `+ 12`, `:1476`). */
    controlColumnTopGap: number;
    /** Stroke width of the mute/camera-off strike line (Flutter `1.5`, `:1534`). */
    controlStrikeWidth: number;
    /** Height of the top scrim band below the safe-area inset (Flutter `viewPadding.top + 80`, `:886`). */
    topOverlayHeight: number;
    /** Size of the watcher-count eye icon (Flutter `liveStatsIconSizeSmall = 14`, `:1109`). */
    statsIconSize: number;
    /** Vertical padding inside the watcher-count pill (Flutter `top/bottom: 3`, `:1101/:1103`). */
    statsPillPaddingVertical: number;
    /** Horizontal padding inside the watcher-count pill (Flutter `left/right: 8`, `:1100/:1102`). */
    statsPillPaddingHorizontal: number;
    /** Gap between the eye icon and the count (Flutter `left: 4`, `:1114`). */
    statsGap: number;
  };
  /**
   * Sizing for the two app-level full-screen blocking overlays
   * (`BudgetExceededOverlay` / `AccountSuspendedOverlay` — web port of Flutter
   * `AppOverlay.budgetExceededOverlay()` / `accountSuspendedOverlay()`,
   * `overlay_widget.dart:14-191`).
   */
  appOverlay: {
    /**
     * Backdrop blur radius of the overlay scrim. Flutter parity: 10 px on both
     * platforms (`DimensionConstants.overlayBlurX/Y`, fed to the
     * `BackdropFilter` at `overlay_widget.dart:24-28` / `:122-126`). CSS
     * `blur(Xpx)` is the closest web equivalent of Flutter's sigma.
     */
    blur: number;
    /**
     * Max width of the centred title / body / button column inside the
     * overlay. Web-only sizing (no Flutter parity — Flutter's phone viewport
     * needs no cap): keeps the copy from stretching edge-to-edge across a wide
     * desktop browser window.
     */
    contentMaxWidth: number;
  };
  /**
   * Sizing shared by the app's authentication surfaces — the `/login` page
   * (`LoginPage` and its `components/` blocks, web port of Flutter
   * `login_screen.dart`) and the `/delete-account` re-auth section, which
   * renders the same shared `ProviderSignInButtons` column and mirrors the
   * login sign-in overlay.
   *
   * Deliberately identical on both tiers. The two max-widths are readability
   * caps that replace the fixed Tailwind utilities the login page used before
   * (`max-w-md` = 28rem = 448 px, `max-w-sm` = 24rem = 384 px); those never
   * varied by breakpoint either, so keeping the tiers equal makes the
   * refactor a pure token swap with zero visual delta. A blur radius is not a
   * scaled size — same tier-identical decision as `appOverlay.blur` and
   * `border.hairline`.
   */
  auth: {
    /**
     * Max width of the login page's centred content column — logo,
     * propositions, provider buttons and terms notice (replaces the former
     * `max-w-md`). Read only by `LoginPage`; `/delete-account` owns its own
     * page shell.
     */
    contentMaxWidth: number;
    /**
     * Max width of the provider-buttons column (replaces the former
     * `max-w-sm`). Consumed by `LoginProviderButtons` / `DevEmailLoginForm`
     * and by `DeleteAccountLoginSection`, which renders the same shared
     * `ProviderSignInButtons` column.
     */
    buttonsMaxWidth: number;
    /**
     * Backdrop blur radius of the sign-in loading overlay, on both `LoginPage`
     * and `DeleteAccountPage`. Flutter parity: 5 px
     * (`DimensionConstants.bottomSheetBlurSigmaX/Y`, fed to the
     * `BackdropFilter` at `login_screen.dart:497-512`). CSS `blur(Xpx)` is the
     * closest web equivalent of Flutter's sigma.
     */
    overlayBlur: number;
  };
}

/**
 * Mobile responsive design values
 */
const MOBILE_RESPONSIVE: ResponsiveDesign = {
  avatar: {
    xs: 24,
    small: 32,
    medium: 40,
    large: 56,
    xlarge: 80,
    xxlarge: 120,
    xxxlarge: 160,
  },
  border: {
    hairline: 0.3,
    thin: 1,
    medium: 2,
    thick: 3,
  },
  opacity: {
    disabled: 0.5,
  },
  borderRadius: {
    sm: 4,
    base: 8,
    md: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    'round': '50%',
  },
  font: {
    xs: '10px',
    sm: '12px',
    base: '14px',
    menu: '20px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '24px',
    '5xl': '48px',
    '10xl': '96px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  spacing: {
    xxxs: 1,
    xxs: 2,
    xs: 4,
    sm: 8,
    base: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
  },
  gap: {
    xs: 4,
    sm: 8,
    base: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    '4xl': 48,
    '5xl': 64,
    '6xl': 80,
  },
  icon: {
    xxs: 10,
    xs: 12,
    small: 16,
    medium: 20,
    large: 24,
    xlarge: 32,
    xxlarge: 48,
  },
  action: {
    imageSize: 32,
  },
  button: {
    size: {
      xxs: 20,
      xs: 28,
      small: 32,
      medium: 40,
      large: 48,
      xl: 60,
      xxl: 72,
      xxxl: 96,
      sl: 128,
      sl2: 160,
    },
    padding: {
      xs: '8px 4px 8px 4px',
      small: '8px 12px 8px 12px',
      medium: '10px 16px 10px 16px',
      large: '12px 20px 12px 20px',
      xl: '12px 36px',
      xxl: '16px 48px',
      xxxl: '20px 64px',
    },
    borderRadius: {
      xs: 4,
      sm: 8,
      base: 12,
      md: 16,
      lg: 20,
      xl: 24,
    },
    border: '0.5px',
  },
  progressBar: {
    height: 2,
  },
  bottomSheet: {
    radius: 20,
    maxHeight: '75dvh',
    topBar: {
      dragIndicator: {
        width: '40px',
        height: '4px',
        borderRadius: '2px',
      },
    },
  },
  dialog: {
    borderRadius: '16px',
    width: '400px',
    maxWidth: '90vw',
    maxHeight: '75dvh',
  },
  infoDialog: {
    maxWidth: '75vw',
  },
  mediaPlayer: {
    maxWidth: '900px',
    maxHeight: '85vh',
  },
  themeSelector: {
    contentPaddingVertical: 48,
  },
  safeArea: {
    top: 'env(safe-area-inset-top, 0px)',
    right: 'env(safe-area-inset-right, 0px)',
    bottom: 'env(safe-area-inset-bottom, 0px)',
    left: 'env(safe-area-inset-left, 0px)',
  },
  stepper: {
    progressHeight: 6,
    progressGap: 4,
    progressBorderRadius: 3,
  },
  form: {
    textareaHeight: 100,
  },
  contentActionsSheet: {
    fixedHeight: '60dvh',
  },
  userActionsSheet: {
    fixedHeight: '60dvh',
  },
  languageSelectorSheet: {
    fixedHeight: '40dvh',
  },
  reportContentSheet: {
    fixedHeight: '75dvh',
  },
  reportUserSheet: {
    fixedHeight: '75dvh',
  },
  appealUserReportSheet: {
    fixedHeight: '75dvh',
  },
  searchUsersSheet: {
    width: '30%',
  },
  selectContentTypeSheet: {
    width: '90dvw',
    fixedHeight: '75dvh',
  },
  addContentSheet: {
    width: '90dvw',
    fixedHeight: '95dvh',
  },
  addFeedback: {
    sheetFixedHeight: '85dvh',
    localMediaMaxHeight: '60dvh',
  },
  dropZone: {
    height: 160,
    padding: 24,
    borderRadius: 12,
  },
  videoPreview: {
    thumbnailSize: 200,
  },
  topBar: {
    height: 48,
    backdropBlur: 24,
    activeLetterSpacing: 0.8,
  },
  appBar: {
    backdropBlur: 10,
  },
  separator: {
    vertical: 2,
    horizontal: 2,
  },
  divider: {
    small: 0.5,
    big: 1,
  },
  walletCard: {
    minHeight: 200,
    maxWidth: 500,
  },
  toggle: {
    thumbPadding: 2,
    shadowBlur: 3,
    shadowOffset: 1,
  },
  flexibleHeader: {
    height: 100,
    borderRadius: 0,
    iconContainerSize: 80,
    iconContainerBorderRadius: 16,
  },
  list: {
    defaultItemHeight: 88,
  },
  chat: {
    strikeWidth: 1.5,
    overlayBlur: 10,
    defaultItemHeight: 72,
    bubbleMinWidth: 40,
    bubbleMaxWidth: '75vw',
    scrolledAwayThreshold: 150,
  },
  community: {
    defaultItemHeight: 96,
    scrolledAwayThreshold: 150,
  },
  videoCall: {
    pipWidth: 96,
    pipInset: 16,
    pipBorderRadius: 8,
    controlIconSize: 28,
    controlHitSize: 52,
    controlGap: 8,
    controlBarPaddingY: 20,
    controlBarPaddingX: 24,
    controlBarFloatGap: 16,
    controlBarRadius: 40,
    controlStrikeWidth: 1.5,
    giftIconSize: 28,
    giftButtonPadding: 14,
  },
  liveStreaming: {
    controlIconSize: 28,
    controlButtonSize: 44,
    controlColumnGap: 20,
    controlColumnRightInset: 12,
    controlColumnTopGap: 12,
    controlStrikeWidth: 1.5,
    topOverlayHeight: 80,
    statsIconSize: 14,
    statsPillPaddingVertical: 3,
    statsPillPaddingHorizontal: 8,
    statsGap: 4,
  },
  appOverlay: {
    blur: 10,
    contentMaxWidth: 480,
  },
  auth: {
    // Deliberately identical to desktop — see the `auth` group JSDoc on the
    // ResponsiveDesign interface.
    contentMaxWidth: 448,
    buttonsMaxWidth: 384,
    overlayBlur: 5,
  },
} as const;

/**
 * Desktop responsive design values
 */
const DESKTOP_RESPONSIVE: ResponsiveDesign = {
  avatar: {
    xs: 36,
    small: 48,
    medium: 56,
    large: 72,
    xlarge: 96,
    xxlarge: 144,
    xxxlarge: 192,
  },
  border: {
    // Deliberately identical to mobile: a hairline divider is a sub-pixel sliver
    // at every breakpoint (the other weights do scale up).
    hairline: 0.3,
    thin: 1.5,
    medium: 2.5,
    thick: 4,
  },
  opacity: {
    disabled: 0.5,
  },
  borderRadius: {
    sm: 6,
    base: 10,
    md: 14,
    lg: 18,
    xl: 22,
    '2xl': 28,
    'round': '50%',
  },
  font: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    menu: '24px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '5xl': '64px',
    '10xl': '128px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  spacing: {
    xxxs: 1,
    xxs: 4,
    xs: 6,
    sm: 10,
    base: 16,
    lg: 20,
    xl: 28,
    '2xl': 36,
    '3xl': 48,
  },
  gap: {
    xs: 6,
    sm: 10,
    base: 16,
    lg: 20,
    xl: 28,
    '2xl': 36,
    '3xl': 48,
    '4xl': 64,
    '5xl': 80,
    '6xl': 96,
  },
  icon: {
    xxs: 12,
    xs: 16,
    small: 20,
    medium: 24,
    large: 28,
    xlarge: 40,
    xxlarge: 48,
  },
  action: {
    imageSize: 40,
  },
  button: {
    size: {
      xxs: 20,
      xs: 28,
      small: 36,
      medium: 44,
      large: 52,
      xl: 60,
      xxl: 72,
      xxxl: 96,
      sl: 180,
      sl2: 212,
    },
    padding: {
      xs: '4px 4px 4px 4px',
      small: '8px 12px 8px 12px',
      medium: '10px 16px 10px 16px',
      large: '12px 20px 12px 20px',
      xl: '16px 40px',
      xxl: '20px 80px',
      xxxl: '24px 120px',
    },
    borderRadius: {
      xs: 4,
      sm: 8,
      base: 12,
      md: 16,
      lg: 20,
      xl: 24,
    },
    border: '0.5px',
  },
  progressBar: {
    height: 2,
  },
  bottomSheet: {
    radius: 20,
    maxHeight: '75vh',
    topBar: {
      dragIndicator: {
        width: '40px',
        height: '4px',
        borderRadius: '2px',
      },
    },
  },
  dialog: {
    borderRadius: '16px',
    width: '500px',
    maxWidth: '90vw',
    maxHeight: '75vh',
  },
  infoDialog: {
    maxWidth: '75vw',
  },
  mediaPlayer: {
    maxWidth: '900px',
    maxHeight: '85vh',
  },
  themeSelector: {
    contentPaddingVertical: 56,
  },
  /**
   * Safe area insets for devices with notches, home indicators, etc.
   * These are CSS string values using env() that can be used in inline styles.
   * Values are dynamically evaluated by the browser and update on orientation change.
   *
   * @example
   * paddingTop: responsive.safeArea.top
   * paddingTop: `calc(${responsive.safeArea.top} + 16px)`
   */
  safeArea: {
    top: 'env(safe-area-inset-top, 0px)',
    right: 'env(safe-area-inset-right, 0px)',
    bottom: 'env(safe-area-inset-bottom, 0px)',
    left: 'env(safe-area-inset-left, 0px)',
  },
  stepper: {
    progressHeight: 6,
    progressGap: 12,
    progressBorderRadius: 3,
  },
  form: {
    textareaHeight: 120,
  },
  contentActionsSheet: {
    fixedHeight: '60dvh',
  },
  userActionsSheet: {
    fixedHeight: '60dvh',
  },
  languageSelectorSheet: {
    fixedHeight: '40dvh',
  },
  reportContentSheet: {
    fixedHeight: '75dvh',
  },
  reportUserSheet: {
    fixedHeight: '75dvh',
  },
  appealUserReportSheet: {
    fixedHeight: '75dvh',
  },
  searchUsersSheet: {
    width: '30%',
  },
  selectContentTypeSheet: {
    width: '60dvw',
    fixedHeight: '75dvh',
  },
  addContentSheet: {
    width: '60dvw',
    fixedHeight: '85vh',
  },
  addFeedback: {
    sheetFixedHeight: '85dvh',
    localMediaMaxHeight: '60dvh',
  },
  dropZone: {
    height: 200,
    padding: 32,
    borderRadius: 16,
  },
  videoPreview: {
    thumbnailSize: 280,
  },
  topBar: {
    height: 48,
    backdropBlur: 24,
    activeLetterSpacing: 0.8,
  },
  appBar: {
    backdropBlur: 10,
  },
  separator: {
    vertical: 2,
    horizontal: 2,
  },
  divider: {
    small: 0.5,
    big: 1,
  },
  walletCard: {
    minHeight: 260,
    maxWidth: 600,
  },
  toggle: {
    thumbPadding: 2,
    shadowBlur: 4,
    shadowOffset: 1,
  },
  flexibleHeader: {
    height: 100,
    borderRadius: 8,
    iconContainerSize: 96,
    iconContainerBorderRadius: 18,
  },
  list: {
    defaultItemHeight: 88,
  },
  chat: {
    strikeWidth: 1.5,
    overlayBlur: 10,
    defaultItemHeight: 88,
    bubbleMinWidth: 48,
    bubbleMaxWidth: '75vw',
    scrolledAwayThreshold: 300,
  },
  community: {
    defaultItemHeight: 112,
    scrolledAwayThreshold: 300,
  },
  videoCall: {
    pipWidth: 160,
    pipInset: 24,
    pipBorderRadius: 12,
    controlIconSize: 32,
    controlHitSize: 64,
    controlGap: 8,
    controlBarPaddingY: 24,
    controlBarPaddingX: 32,
    controlBarFloatGap: 24,
    controlBarRadius: 60,
    controlStrikeWidth: 1.5,
    giftIconSize: 32,
    giftButtonPadding: 16,
  },
  liveStreaming: {
    controlIconSize: 32,
    controlButtonSize: 52,
    controlColumnGap: 24,
    controlColumnRightInset: 16,
    controlColumnTopGap: 16,
    controlStrikeWidth: 1.5,
    topOverlayHeight: 96,
    statsIconSize: 18,
    statsPillPaddingVertical: 4,
    statsPillPaddingHorizontal: 10,
    statsGap: 6,
  },
  appOverlay: {
    blur: 10,
    contentMaxWidth: 640,
  },
  auth: {
    // Deliberately identical to mobile — see the `auth` group JSDoc on the
    // ResponsiveDesign interface.
    contentMaxWidth: 448,
    buttonsMaxWidth: 384,
    overlayBlur: 5,
  },
} as const;

/**
 * Hook to get current platform (mobile or desktop)
 * Re-renders on viewport resize
 */
const useResponsivePlatform = (): Platform => {
  if (typeof window === 'undefined') return 'desktop';

  const [platform, setPlatform] = React.useState<Platform>(
    isMobileViewport() ? 'mobile' : 'desktop'
  );

  React.useEffect(() => {
    const handleResize = () => {
      const newPlatform = isMobileViewport() ? 'mobile' : 'desktop';
      if (newPlatform !== platform) {
        setPlatform(newPlatform);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [platform]);

  return platform;
};

/**
 * Hook to detect landscape orientation
 * Re-renders on viewport resize
 *
 * @returns true if viewport width is greater than height (landscape), false otherwise (portrait)
 *
 * @example
 * const isLandscape = useIsLandscape();
 * <div className={isLandscape ? 'flex-row' : 'flex-col'} />
 */
export const useIsLandscape = (): boolean => {
  if (typeof window === 'undefined') return false;

  const [isLandscape, setIsLandscape] = React.useState<boolean>(
    isLandscapeViewport()
  );

  React.useEffect(() => {
    const handleResize = () => {
      const newIsLandscape = isLandscapeViewport();
      if (newIsLandscape !== isLandscape) {
        setIsLandscape(newIsLandscape);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLandscape]);

  return isLandscape;
};

/**
 * Main hook to get responsive design values
 * Automatically detects platform and returns appropriate values
 *
 * @example
 * const responsive = useResponsiveDesign();
 * <div style={{
 *   width: responsive.avatar.small,
 *   padding: responsive.spacing.base,
 *   fontSize: responsive.font.base
 * }} />
 */
export const useResponsiveDesign = (): ResponsiveDesign => {
  const platform = useResponsivePlatform();
  return platform === 'mobile' ? MOBILE_RESPONSIVE : DESKTOP_RESPONSIVE;
};

/**
 * Viewport dimensions returned by useViewportDimensions
 */
export interface ViewportDimensions {
  width: number;
  height: number;
}

/**
 * Hook to get accurate viewport dimensions
 * Uses visualViewport API when available for accurate mobile measurements
 * (accounts for browser chrome, virtual keyboard, etc.)
 *
 * Re-renders on viewport resize, orientation change, and visualViewport changes.
 *
 * @example
 * const { width, height } = useViewportDimensions();
 * <div style={{ height: `${height}px` }} />
 */
export const useViewportDimensions = (): ViewportDimensions => {
  const getViewportDimensions = React.useCallback((): ViewportDimensions => {
    if (typeof window === 'undefined') {
      return { width: 0, height: 0 };
    }
    // visualViewport provides accurate dimensions on mobile (accounts for browser chrome)
    return {
      width: window.visualViewport?.width ?? window.innerWidth,
      height: window.visualViewport?.height ?? window.innerHeight,
    };
  }, []);

  const [dimensions, setDimensions] = React.useState<ViewportDimensions>(getViewportDimensions);

  React.useEffect(() => {
    const updateDimensions = () => {
      const newDimensions = getViewportDimensions();
      setDimensions(prev => {
        // Only update if dimensions actually changed
        if (prev.width !== newDimensions.width || prev.height !== newDimensions.height) {
          return newDimensions;
        }
        return prev;
      });
    };

    // Listen to multiple events for comprehensive coverage
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);
    window.visualViewport?.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
      window.visualViewport?.removeEventListener('resize', updateDimensions);
    };
  }, [getViewportDimensions]);

  return dimensions;
};
